<template>
    <LoadingCard v-if="loading"></LoadingCard>
    <div v-else>
        <header class="content__title pl-0">
            <h1>
                Withholding Periods
            </h1>

            <div class="actions">
                <a class="btn btn-success btn--icon" @click.prevent="showCreateDialog"
                   v-b-tooltip.left title="Add new Withholding Period"><i class="zmdi zmdi-plus"></i></a>
            </div>
        </header>

        <div class="card">
            <div class="bg-primary align-items-center d-flex">
                <h5 class="card-header text-white">All Withholding Periods</h5>
                <b-pagination align="right" class="my-auto mr-2 ml-auto"
                              :total-rows="tableData.length"
                              v-model="pagination.page"
                              :per-page="pagination.limit"
                />
            </div>
            <div class="form-control form-control-lg bg-light form-filter d-flex">
                <VueSelect
                        v-model="country"
                        :options="allCountries"
                        placeholder="Sort by Country"
                        label="name"
                        track-by="id"
                ></VueSelect>
                <VueSelect
                        v-model="chemical"
                        :options="allChemicals"
                        placeholder="Sort by Chemical"
                        label="name"
                        track-by="id"
                ></VueSelect>
                <VueSelect
                        v-model="varietyGroup"
                        :options="allVarietyGroups"
                        placeholder="Sort by Variety Group"
                        label="name"
                        track-by="id"
                ></VueSelect>
            </div>
            <b-table hover stacked="md" :items="tableData" sort-by="name" :fields="table.fields"
                     :per-page="pagination.limit" :current-page="pagination.page"
                     :show-empty="true">

                <template slot="country" slot-scope="data">
                    {{ data.item.country.name}}
                </template>

                <template slot="contamination_period" slot-scope="data">
                    {{ data.item.contamination_period | number_format}} days
                </template>

                <template slot="varietyGroup" slot-scope="data">
                    {{ data.item.varietyGroup.name}}
                </template>

                <template slot="chemical" slot-scope="data">
                    {{ data.item.chemical.name}}
                </template>

                <template slot="badges" slot-scope="data">
                    <span class="badge badge-danger" v-if="data.item.deleted_at">
                    Disabled
                    </span>
                </template>

                <template slot="actions" slot-scope="row">
                    <b-button @click="showEditDialog(row.item)"
                              variant="secondary btn--icon"
                              v-b-tooltip.hover="'Edit Withholding Period'">
                        <i class="zmdi zmdi-edit"></i>
                    </b-button>
                </template>
            </b-table>
        </div>
        <CreateModal :close="closeCreateDialog" v-model="creating" :cropTypeId="cropTypeId"
                     :countries="allCountries" :varietyGroups="allVarietyGroups" :chemicals="allChemicals"
                     v-if="creating" />
        <EditModal :close="closeEditDialog" :withholding-period="editingWithholdingPeriod"
                   :countries="allCountries" :varietyGroups="allVarietyGroups" :chemicals="allChemicals"
                   v-model="editingWithholdingPeriod" v-if="editingWithholdingPeriod" />
    </div>
</template>

<script>
import listActiveIngredientsQuery from '@views/WithholdingPeriods/Browse/queryListChemicalActiveIngredients.graphql';
import listCountriesQuery from '@views/WithholdingPeriods/Browse/queryListCountries.graphql';
import listVarietyGroupsQuery from '@views/WithholdingPeriods/Browse/queryListVarietyGroups.graphql';
import listWithholdingPeriodsQuery from '@views/WithholdingPeriods/Browse/queryListWithholdingPeriods.graphql';

import CreateModal from './CreateModal';
import EditModal from './EditModal';

export default {
  name: 'BrowseWithholdingPeriods',
  props: ['cropTypeId'],
  apollo: {
    withholdingPeriods: {
      query: listWithholdingPeriodsQuery,
      variables() {
        const variables = { input: { include_deleted: true } };

        if (this.cropTypeId) {
          variables.input.crop_type_id = this.cropTypeId;
        }

        return variables;
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          this.withholdingPeriods = data.filterWithholdingPeriods.data;
          this.pagination.total = data.filterWithholdingPeriods.meta.total;
        }
      },
    },
    allCountries: {
      query: listCountriesQuery,
      update: data => data.filterCountries.data,
    },
    allChemicals: {
      query: listActiveIngredientsQuery,
      update: data => data.filterChemicalActiveIngredients.data,
    },
    allVarietyGroups: {
      query: listVarietyGroupsQuery,
      update: data => data.filterVarietyGroups.data,
      variables() {
        if (this.cropTypeId) {
          return { input: { crop_type_id: this.cropTypeId } };
        }
      },
    },
  },
  data() {
    return {
      withholdingPeriods: [],
      editingWithholdingPeriod: null,
      creating: false,
      loading: 0,
      country: null,
      chemical: null,
      varietyGroup: null,
      allChemicals: [],
      allVarietyGroups: [],
      allCountries: [],
      pagination: {
        page: 1,
        limit: this.$tableDefaultPageSize,
        total: 0,
      },
      table: {
        fields: [
          {
            key: 'country',
            label: 'Country',
          },
          {
            key: 'varietyGroup',
            label: 'Variety Group',
          },
          {
            key: 'chemical',
            label: 'Chemical',
          },
          {
            key: 'contamination_period',
            label: 'Contamination Period',
            sortable: true,
          },
          {
            key: 'badges',
            label: 'Statuses',
          },
          {
            key: 'actions',
            label: '',
            tdClass: 'text-right',
          },
        ],
      },
    };
  },
  computed: {
    tableData() {
      return (this.withholdingPeriods || [])
        .filter(item => {
          if (this.chemical && item.chemical.id !== this.chemical.id) {
            return false;
          }

          if (this.country && item.country.id !== this.country.id) {
            return false;
          }

          if (this.varietyGroup && item.varietyGroup.id !== this.varietyGroup.id) {
            return false;
          }

          return true;
        })
        .map(item => ({ ...item }));
    },
  },
  methods: {
    refetch() {
      this.$apollo.queries.withholdingPeriods.refetch();
    },
    showEditDialog(WithholdingPeriod) {
      this.editingWithholdingPeriod = WithholdingPeriod;
    },
    closeEditDialog() {
      this.editingWithholdingPeriod = null;
    },
    showCreateDialog() {
      this.creating = true;
    },
    closeCreateDialog() {
      this.creating = false;
    },
  },
  components: {
    EditModal,
    CreateModal,
  },
};
</script>
